/**
 * Cable authoring kit.
 *
 * Cable manufacturers publish far less hard data than converter or amplifier
 * makers -- many publish none at all. So rather than let each entry invent
 * numbers, this kit DERIVES the three electrical properties that matter from
 * facts that are normally stated or directly observable: conductor gauge,
 * conductor material, and cable geometry. Where a manufacturer does publish
 * real figures, the entry passes them in and they win.
 *
 * The formulas are standard and checkable:
 *
 *   RESISTANCE   R = rho / A per conductor, doubled for the loop. AWG converts
 *                to area by A(mm^2) = 0.012668 * 92^((36-AWG)/19.5), which is
 *                the definition of the gauge. Copper rho = 1.724e-8 ohm.m at
 *                20 C; silver 1.587e-8, i.e. about 8% lower.
 *
 *   INDUCTANCE   For a parallel pair, L ~ 0.4 * ln(2D/d) uH/m where D is
 *                centre spacing and d conductor diameter. Geometry dominates:
 *                a flat ribbon with wide, close conductors lands near
 *                0.05 uH/m, an ordinary twisted pair near 0.6, a widely spaced
 *                pair near 1.0.
 *
 *   CAPACITANCE  Inversely related to the same spacing, so the geometries that
 *                minimise inductance maximise capacitance and vice versa. This
 *                trade-off is real and is why the two figures below always move
 *                in opposite directions.
 *
 * Everything the DSP does with a cable comes from these three numbers. Nothing
 * here encodes a house sound, a material "character", or a directionality.
 */

import type {
  CableElectrical,
  CableGeometry,
  CableProfile,
  CableType,
  ConductorMaterial,
  PriceInfo,
} from '../types/hardware';
import type { Confidence } from '../types/provenance';
import { img, slug } from './kit';

/** Resistivity in ohm-metres at 20 C. */
const RESISTIVITY: Record<ConductorMaterial, number> = {
  ofc: 1.724e-8,
  'occ-copper': 1.71e-8,
  'silver-plated-copper': 1.71e-8, // silver skin only; DC path is the copper core
  'pure-silver': 1.587e-8,
  'silver-gold-alloy': 1.9e-8, // gold raises resistivity relative to pure silver
  'copper-silver-hybrid': 1.68e-8,
  carbon: 3.5e-5, // orders of magnitude higher; only used as a shield or damping layer
  'copper-clad-aluminium': 2.65e-8,
};

/** Cross-sectional area of one conductor in square metres, from AWG. */
export function awgToAreaM2(awg: number): number {
  const mm2 = 0.012668 * Math.pow(92, (36 - awg) / 19.5);
  return mm2 * 1e-6;
}

/** Loop resistance per metre: both conductors in series. */
export function loopResistancePerM(awg: number, material: ConductorMaterial): number {
  const area = awgToAreaM2(awg);
  return (2 * RESISTIVITY[material]) / area;
}

/**
 * Typical per-metre inductance and capacitance by geometry.
 *
 * These are the two ends of the same physical trade-off -- close, wide
 * conductors give low inductance and high capacitance; separated, thin
 * conductors give the reverse -- so they are declared together to keep the
 * relationship honest.
 */
const GEOMETRY_LC: Record<CableGeometry, { uhPerM: number; pfPerM: number }> = {
  ribbon: { uhPerM: 0.05, pfPerM: 620 },
  'flat-parallel': { uhPerM: 0.12, pfPerM: 300 },
  litz: { uhPerM: 0.35, pfPerM: 120 },
  braided: { uhPerM: 0.4, pfPerM: 105 },
  'star-quad': { uhPerM: 0.45, pfPerM: 95 },
  'twisted-pair': { uhPerM: 0.6, pfPerM: 70 },
  concentric: { uhPerM: 0.25, pfPerM: 150 },
  coaxial: { uhPerM: 0.25, pfPerM: 85 },
  'solid-core': { uhPerM: 0.75, pfPerM: 45 },
  'air-tube': { uhPerM: 0.9, pfPerM: 28 },
  shotgun: { uhPerM: 0.55, pfPerM: 80 },
};

/** Sensible default gauges per cable type when a maker does not state one. */
const DEFAULT_GAUGE: Record<CableType, number> = {
  speaker: 14,
  power: 12,
  interconnect: 22,
  usb: 24,
  ethernet: 24,
};

const DEFAULT_LENGTH: Record<CableType, number> = {
  speaker: 3,
  power: 1.5,
  interconnect: 1,
  usb: 1.5,
  ethernet: 1.5,
};

const STANDARD_LENGTHS: Record<CableType, number[]> = {
  speaker: [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10],
  power: [1, 1.5, 2, 2.5, 3],
  interconnect: [0.5, 0.75, 1, 1.5, 2, 3, 5],
  usb: [0.5, 0.75, 1, 1.5, 2, 3, 5],
  ethernet: [0.5, 1, 1.5, 2, 3, 5, 10],
};

export interface CableSpecInput {
  mfr: string;
  model: string;
  cableType: CableType;
  price: PriceInfo;
  /** Length the quoted price applies to, metres. */
  priceLengthM?: number;
  year?: number;
  country?: string;
  conductor: ConductorMaterial;
  geometry: CableGeometry;
  gauge?: number;
  /** Published figures win over derivation. Any subset may be supplied. */
  resistanceOhmPerM?: number;
  inductanceUhPerM?: number;
  capacitancePfPerM?: number;
  characteristicOhms?: number;
  /** Set when the electrical figures above came from the manufacturer. */
  electricalConfidence?: Confidence;
  electricalSource?: string;
  shielding?: string;
  dielectric?: string;
  termination?: string[];
  balanced?: boolean;
  currentRatingA?: number;
  digitalStandard?: string;
  defaultLengthM?: number;
  standardLengthsM?: number[];
  notes?: string;
  tags?: string[];
  confidence?: Confidence;
  provenanceNote?: string;
  accent?: string;
  version?: number;
}

export function makeCable(i: CableSpecInput): CableProfile {
  const id = slug(i.mfr, i.model, `cable-${i.cableType}`);
  const gauge = i.gauge ?? DEFAULT_GAUGE[i.cableType];
  const lc = GEOMETRY_LC[i.geometry];

  const derivedR = loopResistancePerM(gauge, i.conductor);
  const published =
    i.resistanceOhmPerM !== undefined ||
    i.inductanceUhPerM !== undefined ||
    i.capacitancePfPerM !== undefined;

  const electrical: CableElectrical = {
    resistanceOhmPerM: i.resistanceOhmPerM ?? derivedR,
    inductanceUhPerM: i.inductanceUhPerM ?? lc.uhPerM,
    capacitancePfPerM: i.capacitancePfPerM ?? lc.pfPerM,
    characteristicOhms:
      i.characteristicOhms ??
      (i.cableType === 'usb' ? 90 : i.cableType === 'ethernet' ? 100 : undefined),
    confidence: i.electricalConfidence ?? (published ? 'MANUFACTURER_SPEC' : 'DERIVED'),
    source:
      i.electricalSource ??
      (published
        ? 'Manufacturer published figures'
        : `Derived from ${gauge} AWG ${i.conductor} in a ${i.geometry} geometry: loop R from conductor area and resistivity, L and C from geometry norms`),
  };

  return {
    id,
    category: 'cable',
    manufacturer: i.mfr,
    model: i.model,
    price: i.price,
    priceLengthM: i.priceLengthM ?? DEFAULT_LENGTH[i.cableType],
    version: i.version ?? 1,
    revised: '2026-08-23',
    confidence: i.confidence ?? 'ESTIMATED',
    provenanceNote:
      i.provenanceNote ??
      `Construction, conductor material and geometry from published manufacturer descriptions. Electrical figures are ${published ? 'as published' : 'derived from gauge, material and geometry using standard formulas'}; most cable makers do not publish resistance, inductance and capacitance, and none are invented here.`,
    notes: i.notes,
    tags: [...(i.tags ?? []), i.cableType, i.conductor, i.geometry],
    year: i.year,
    countryOfOrigin: i.country,
    image: img(id, i.accent),
    cableType: i.cableType,
    conductor: i.conductor,
    geometry: i.geometry,
    gauge,
    electrical,
    shielding: i.shielding,
    dielectric: i.dielectric,
    termination: i.termination,
    standardLengthsM: i.standardLengthsM ?? STANDARD_LENGTHS[i.cableType],
    defaultLengthM: i.defaultLengthM ?? DEFAULT_LENGTH[i.cableType],
    balancedAvailable: i.balanced,
    currentRatingA: i.currentRatingA,
    digitalStandard: i.digitalStandard,
  };
}

/**
 * Total series impedance a cable contributes at one frequency.
 *
 * Returned as a complex pair so the caller can combine it with an amplifier
 * output impedance and a transducer impedance properly, rather than by adding
 * magnitudes.
 */
export function cableSeriesImpedance(
  e: CableElectrical,
  lengthM: number,
  freqHz: number,
): { re: number; im: number } {
  const r = e.resistanceOhmPerM * lengthM;
  const l = e.inductanceUhPerM * 1e-6 * lengthM;
  return { re: r, im: 2 * Math.PI * freqHz * l };
}

/** Total shunt capacitance in farads for a given run. */
export function cableShuntCapacitance(e: CableElectrical, lengthM: number): number {
  return e.capacitancePfPerM * 1e-12 * lengthM;
}
